/**
 * Consent Request API Service
 *
 * Domain-specific service encapsulating all consent management endpoints.
 * Ensures every network call goes through the shared httpClient with interceptors,
 * matching the architecture guidelines for backend communication.
 */

import type { AxiosRequestConfig } from 'axios';
import { httpClient } from './http-client.service';
import type {
  ConsentAccountDetailsResponse,
  ConsentAnalyticsDownloadPayload,
  ConsentAnalyticsListResponse,
  ConsentCreatePayload,
  ConsentCreateResponse,
  ConsentDownloadListResponse,
  ConsentEndUserListPayload,
  ConsentEndUserListResponse,
  ConsentRequestListParams,
  ConsentRequestListResponse,
  ConsentTimelineAPIResponse,
  ConsentRefreshResponse,
  ConsentSmsResponse,
  StateListResponse,
  GetAllFIDataPayload,
  GetAllFIDataResponse,
} from '../types/consent';

class ConsentRequestAPIService {
  /**
   * Fetch consent requests with pagination and optional filters.
   */
  async getConsentRequests(params: ConsentRequestListParams): Promise<ConsentRequestListResponse> {
    const response = await httpClient.post<ConsentRequestListResponse>('consent/request/list', params);
    return response.data;
  }

  /**
   * Download consent requests for a given date range (CSV data).
   */
  async getConsentRequestsByDate(payload: { from: string; to: string }): Promise<ConsentDownloadListResponse> {
    const response = await httpClient.post<ConsentDownloadListResponse>('consent/download/list', payload);
    return response.data;
  }

  /**
   * Retrieve consent artefacts and account details for a given consent ID.
   */
  async getConsentAccountDetails(payload: { consentId: string }): Promise<ConsentAccountDetailsResponse> {
    const response = await httpClient.post<ConsentAccountDetailsResponse>('consent/artefacts/accountsList', payload);
    return response.data;
  }

  /**
   * Get FI data for specific accounts by their link reference numbers.
   */
  async getAllFIData(payload: GetAllFIDataPayload): Promise<GetAllFIDataResponse> {
    const response = await httpClient.post<GetAllFIDataResponse>('getAllFIData', payload);
    return response.data;
  }

  /**
   * Download FI data in JSON format.
   */
  async downloadFiDataJson(payload: { consentID: string; downloadjson?: boolean }): Promise<any> {
    const response = await httpClient.post('getAllFIData/json', payload);
    return response.data;
  }

  /**
   * Download FI data in PDF format.
   */
  async downloadFiDataPdf(payload: { consentID: string; linkRefNumber: string[] }): Promise<Blob> {
    const config: AxiosRequestConfig = { responseType: 'blob' };
    const response = await httpClient.post<Blob>('getallfidataPdf', payload, config);
    return response.data;
  }

  /**
   * Download FI data in XML format.
   */
  async downloadFiDataXml(payload: { consentID: string }): Promise<any> {
    const response = await httpClient.post('xml/getfidata', payload);
    return response.data;
  }

  /**
   * Fetch analytics configuration options.
   */
  async getAnalyticsOptions(): Promise<ConsentAnalyticsListResponse> {
    const response = await httpClient.get<ConsentAnalyticsListResponse>('get/analytics');
    return response.data;
  }

  /**
   * Download analytics output (JSON or Excel).
   * Transforms payload to match Angular's format with underscored keys.
   */
  async downloadAnalytics(payload: ConsentAnalyticsDownloadPayload): Promise<Blob | any> {
    // Transform to Angular-compatible format with underscored keys
    const requestPayload = {
      consentID: payload.consentID,
      linkRefNumber: payload.linkRefNumber,
      analytics_type: payload.analyticsType,
      analytics_version: payload.analyticsVersion,
    };

    if (payload.analyticsFormat === 'EXCEL') {
      const response = await httpClient.post('analytics/request', requestPayload, {
        responseType: 'blob',
      } satisfies AxiosRequestConfig);
      return response.data;
    }

    const response = await httpClient.post('analytics/request', requestPayload);
    return response.data;
  }

  /**
   * Trigger FI request mode for a consent ID.
   */
  async triggerFiRequest(payload: { consentID: string }): Promise<any> {
    const response = await httpClient.post('fi/request', payload);
    return response.data;
  }

  /**
   * Refresh consent status (fetch latest details from backend).
   */
  async refreshConsent(payload: { ConsentHandle: string[] }): Promise<ConsentRefreshResponse> {
    const response = await httpClient.post<ConsentRefreshResponse>('consent/status/fetch', payload);
    return response.data;
  }

  /**
   * Resend SMS alert for consent.
   */
  async resendSms(payload: { consenthandle: string }): Promise<ConsentSmsResponse> {
    const response = await httpClient.post<ConsentSmsResponse>('consent/sendsms', payload);
    return response.data;
  }

  /**
   * Create consent request (v3 endpoint).
   */
  async createConsent(payload: ConsentCreatePayload): Promise<ConsentCreateResponse> {
    const response = await httpClient.post<ConsentCreateResponse>('v3/requestconsent', payload);
    return response.data;
  }

  /**
   * Create consent request (v2 endpoint - legacy journey).
   */
  async createConsentV2(payload: ConsentCreatePayload): Promise<ConsentCreateResponse> {
    const response = await httpClient.post<ConsentCreateResponse>('v2/requestconsent', payload);
    return response.data;
  }

  /**
   * Create consent request for end-user flow.
   */
  async createConsentForEndUser(payload: ConsentCreatePayload): Promise<ConsentCreateResponse> {
    const response = await httpClient.post<ConsentCreateResponse>('web/consent/request', payload);
    return response.data;
  }

  /**
   * Fetch end user details for a mobile number.
   */
  async getEndUserDetails(payload: ConsentEndUserListPayload): Promise<ConsentEndUserListResponse> {
    const response = await httpClient.post<ConsentEndUserListResponse>('web/consent/enduser/list', payload);
    return response.data;
  }

  /**
   * Fetch list of states / union territories.
   */
  async getStates(): Promise<StateListResponse> {
    const response = await httpClient.get<StateListResponse>('states/get');
    return response.data;
  }

  /**
   * Fetch consolidated timeline with all events in chronological order.
   * Single endpoint that replaces the need for multiple API calls.
   */
  async getTimelineV2(payload: { consent_handle: string }): Promise<ConsentTimelineAPIResponse> {
    const response = await httpClient.post<{ status: string; ver: string; data: ConsentTimelineAPIResponse }>('consentdatatimelinev2', payload);
    return response.data.data; // Extract actual timeline data from wrapper
  }

  // ============ MASTER DATA APIs for Filter Dropdowns ============

  /**
   * Fetch list of all products/templates for filter dropdown.
   * Response structure: { data: { rows: [...] } }
   */
  async getProductsList(): Promise<{ data: { rows: Array<{ product_code: string; product_name: string; status: string }> } }> {
    const response = await httpClient.post<{ data: { rows: Array<{ product_code: string; product_name: string; status: string }> } }>('productslist', {});
    return response.data;
  }

  /**
   * Fetch list of all purpose codes for filter dropdown.
   * Response structure: { data: { rows: [...] } }
   */
  async getPurposesList(): Promise<{ data: { rows: Array<{ purpose_code: string; purpose_text: string }> } }> {
    const response = await httpClient.post<{ data: { rows: Array<{ purpose_code: string; purpose_text: string }> } }>('purposelist', {});
    return response.data;
  }

  /**
   * Fetch list of all account aggregators for filter dropdown.
   * Response structure: { data: { rows: [...] } }
   */
  async getAggregatorsList(): Promise<{ data: { rows: Array<{ account_aggregator_id: string; account_aggregator_name?: string }> } }> {
    const response = await httpClient.post<{ data: { rows: Array<{ account_aggregator_id: string; account_aggregator_name?: string }> } }>('accountaggregatordetailslist', {});
    return response.data;
  }
}

export const consentRequestAPI = new ConsentRequestAPIService();
